import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';
import { getStaff } from '../api/financeApi';
import { assetUrl } from '../api/financeClient';
import { useFinance } from '../financeContext';

// Delivery person / agent / reservation-manager profile popup — selfie, name, phone, role + KYC
// badges. Extracted from the vertical apps' UserDetailsModal so finance (which never had a
// UserDetailsModal, and real_estate's admin has none) can see the same. Read-only; an admin gets
// a "Manage in dashboard" jump via onOpenExternal. Backend: GET /api/admin/staff/:id (lean
// publicUser, spreads kyc).
export default function StaffDetailsModal({ staffId, show, onHide, onOpenExternal, isAdmin }) {
  const { wording } = useFinance();
  const ROLE_LABEL = {
    delivery: wording.deliveryPerson || 'Delivery / agent',
    reservation_manager: 'Reservation manager',
    owner: 'Rental owner',
    admin: 'Website owner'
  };
  const [u, setU] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !staffId) return undefined;
    let cancelled = false;
    setU(null); setError(''); setLoading(true);
    getStaff(staffId)
      .then((data) => { if (!cancelled) setU(data); })
      .catch(() => { if (!cancelled) setError('Could not load this person.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, staffId]);

  const kyc = u?.kyc || {};
  const roles = u?.effectiveRoles || u?.roles || (u?.role ? [u.role] : []);

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{u ? u.name : 'Person details'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" role="status" />}
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {u && (
          <>
            <div className="d-flex gap-3 align-items-start mb-3">
              {kyc.selfieUrl ? (
                <img
                  src={assetUrl(kyc.selfieUrl)}
                  alt={`${u.name} selfie`}
                  style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <div
                  className="bg-secondary-subtle d-flex align-items-center justify-content-center text-muted"
                  style={{ width: 72, height: 72, borderRadius: 8, fontSize: 24 }}
                  aria-hidden="true"
                >
                  {(u.name || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="fw-semibold">{u.name}</div>
                {u.phone && <div className="small">📞 {u.phone}</div>}
                {u.email && <div className="small text-muted">{u.email}</div>}
                {kyc.selfieUrl && (
                  <a className="small" href={assetUrl(kyc.selfieUrl)} target="_blank" rel="noreferrer">View selfie</a>
                )}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-1 mb-2">
              {roles.map((r) => <Badge key={r} bg="secondary">{ROLE_LABEL[r] || r}</Badge>)}
              <Badge bg={u.status === 'disabled' ? 'danger' : 'success'}>{u.status || 'active'}</Badge>
              {kyc.verified
                ? <Badge bg="success">KYC verified</Badge>
                : <Badge bg="warning" text="dark">KYC not verified</Badge>}
              {kyc.aadhaarNumber && <Badge bg="light" text="dark" className="border">Aadhaar on file</Badge>}
              {(kyc.drivingLicenceNumber || kyc.drivingLicenceUrl) && (
                <Badge bg="light" text="dark" className="border">Licence on file</Badge>
              )}
            </div>

            {u.serviceArea && <div className="small mb-1">Service area: {u.serviceArea}</div>}
            {u.onboardedByOwnerName && (
              <div className="small text-muted mb-1">Onboarded by {u.onboardedByOwnerName}</div>
            )}
            {u.createdAt && <div className="small text-muted">Joined {dateStr(u.createdAt)}</div>}

            {kyc.aadhaarCardUrl && (
              <div className="small mt-2">
                <a href={assetUrl(kyc.aadhaarCardUrl)} target="_blank" rel="noreferrer">View Aadhaar card</a>
              </div>
            )}
            {kyc.drivingLicenceUrl && (
              <div className="small">
                <a href={assetUrl(kyc.drivingLicenceUrl)} target="_blank" rel="noreferrer">View driving licence</a>
              </div>
            )}

            {isAdmin && onOpenExternal && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary mt-3"
                onClick={() => onOpenExternal('staff', u)}
              >
                Manage in dashboard
              </button>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
